import { useCallback, useMemo } from 'react';
import { parseUTCdateTime } from 'utils/dates';
import { useSelectEval, useWorkspaceCalculations } from './hooks';
import { evalDisplayName } from './utils';
import { EvalListTable, type EvalListRow } from './eval-list-table';

interface WorkspacePageProps {
  /** The selected workspace folder whose evals the table lists. */
  workspaceId: number;
}

/** The management table's three columns (lead-decided; all other calc fields deferred). */
const HEADERS = [
  { key: 'name', header: 'Name' },
  { key: 'createdBy', header: 'Created by' },
  { key: 'createdAt', header: 'Created at' },
] as const;

/**
 * The center management body shown when a workspace folder is selected (mock §6) — a read-only
 * table of the workspace's evals. Three columns: Name (the calc's `description`), Created by
 * (`runByUser.name`), Created at (formatted). The workspace name + rename live in `CenterHeader`,
 * so this is just the table. **Saved evals are excluded** — they move to the Saved Evaluations
 * folder (P8 decision 4), matching the tree.
 *
 * Each row routes to that eval via `useSelectEval` (the same navigation the tree leaf uses). The
 * calc list comes from `useWorkspaceCalculations` (shares the tree's React Query key → a cache hit
 * when the folder is already loaded). The table shell (the house `DataTable` from
 * `components/data-table`, with a keyboard-accessible link-cell nav + skeleton/error/empty) is the
 * shared {@link EvalListTable} — the saved-evals browse feeds the same.
 *
 * @param {WorkspacePageProps} props - The selected workspace id.
 * @return {JSX.Element} The eval-metadata table (or its loading / error state).
 */
export function WorkspacePage({ workspaceId }: WorkspacePageProps) {
  const selectEval = useSelectEval();
  const { byWorkspace, statusByWorkspace } = useWorkspaceCalculations([
    workspaceId,
  ]);
  const calcs = byWorkspace[workspaceId];
  const workspaceStatus = statusByWorkspace[workspaceId];

  // Stable across renders so `EvalListTable`'s `columns` memo (keyed on `onRowClick`) — and the
  // DataTable's own column model — don't rebuild every render.
  const onRowClick = useCallback(
    (id: string) => selectEval(workspaceId, Number(id)),
    [selectEval, workspaceId]
  );

  const rows = useMemo<EvalListRow[]>(
    () =>
      (calcs ?? [])
        // A saved eval no longer lists under its workspace — exclude it here so the table
        // matches the tree leaves (P8 decision 4).
        .filter((calc) => !calc.saved)
        .map((calc) => ({
          id: String(calc.id),
          name: evalDisplayName(
            calc.id,
            calc.description,
            calc.runByUser?.name
          ),
          createdBy: calc.runByUser?.name ?? '',
          createdAt: parseUTCdateTime(calc.createdAt),
        })),
    [calcs]
  );

  return (
    <EvalListTable
      headers={HEADERS}
      rows={rows}
      status={workspaceStatus?.status ?? 'loading'}
      errorMessage="Couldn’t load this workspace’s evaluations."
      onRetry={workspaceStatus?.onRetry}
      linkColumnKey="name"
      onRowClick={onRowClick}
    />
  );
}
